import {useEffect} from 'react'
import {useNavigate, useSearchParams, Link} from 'react-router'
import {useAuth} from '@/context/AuthContext'
import {saveRedirectAfterLogin} from '@/utils/functions'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'

/**
 * The auth gate for `/rides/:rideId/phone-booking-confirmation` — the ONE route that is
 * deliberately outside `ProtectedRoute`.
 *
 * Every other page in this dashboard belongs to someone who signed in. This one is a link
 * an agent read down the phone to a passenger who may have no account they know about, no
 * password, and no reason to recognise a sign-in screen. Bouncing them to `/signin` ends
 * the booking: there is nothing there they can do. So the route is public and proves the
 * visitor the way the phone desk already can — an OTP to the number on the booking, via
 * `/authorize`.
 *
 * Three outcomes, same shape as RefAuthGate's:
 *
 *  - Signed in → render the page. (RefAuthGate has already checked the ref is theirs.)
 *  - Not signed in, ref in hand → `/authorize?ref=…`, which texts the code and comes back.
 *  - Not signed in, no ref → we cannot text anyone: nothing identifies the booking. Say so
 *    and offer sign-in as a LINK. Never a redirect — that is the bounce this route exists
 *    to avoid.
 *
 * RefAuthGate normally catches the second case before this ever renders. This is the gate
 * for what it can't see: a session that dies while the page is open (a 401 clears the
 * token and AuthContext flips `isAuthenticated`), or a reload after that.
 */
// Refs this gate has already sent to /authorize since the app loaded.
//
// One bounce is the feature. A SECOND for the same ref means the passenger verified a code
// and the session still didn't survive landing here — a 401 on the page's own requests
// clears the cookie, AuthContext flips, and we would send them back for another code, and
// another, with each screen looking exactly like the first. That reads as "the OTP does
// nothing". So the second time we stop and say so. Module-level on purpose: it must
// outlive this component's mounts but not a page reload, which is a fresh, deliberate try.
const bounced = new Set()

export default function PhoneBookingRouteGate({children}) {
    const {isAuthenticated} = useAuth()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    // Present only on ARRIVAL, and only when RefAuthGate has not already dealt with it —
    // it is stripped the moment the passenger is through. So a session that lapses later
    // falls to the no-ref branch below, which sends them back to their message for a fresh
    // link rather than pretending it can text a code with nothing to key it on.
    const ref = searchParams.get('ref')
    const looping = !!ref && !isAuthenticated && bounced.has(ref)

    useEffect(() => {
        if (isAuthenticated) {
            if (ref) bounced.delete(ref)
            return
        }
        if (!ref || bounced.has(ref)) return
        bounced.add(ref)
        saveRedirectAfterLogin(
            `${window.location.pathname}${window.location.search}${window.location.hash}`,
        )
        navigate(`/authorize?ref=${encodeURIComponent(ref)}`, {replace: true})
    }, [isAuthenticated, ref, navigate])

    if (isAuthenticated) return children

    if (looping) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-md text-center">
                    <h1 className="text-xl font-semibold text-(--text-primary)">
                        We couldn't keep you signed in
                    </h1>
                    <p className="mt-3 text-sm text-(--text-secondary)">
                        Your code was accepted, but the session didn't survive loading this
                        booking. Reload the page to try once more — if it keeps happening,
                        call the number you booked on and we'll confirm the ride for you.
                    </p>
                    <Link
                        to="/signin"
                        className="mt-6 inline-block text-sm font-medium underline text-(--text-primary)"
                    >
                        Sign in instead
                    </Link>
                </div>
            </div>
        )
    }

    if (ref) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader/>
            </div>
        )
    }

    return (
        <div className="min-h-[60vh] flex items-center justify-center px-4">
            <div className="max-w-md text-center">
                <h1 className="text-xl font-semibold text-(--text-primary)">
                    Open this from the link we sent you
                </h1>
                <p className="mt-3 text-sm text-(--text-secondary)">
                    We text a code to the number on your booking to confirm it is you. This
                    address is missing the part that tells us which booking to check, so
                    reopen the link from your message — or call the number you booked on and
                    we will resend it.
                </p>
                <Link
                    to="/signin"
                    className="mt-6 inline-block text-sm font-medium underline text-(--text-primary)"
                >
                    Sign in instead
                </Link>
            </div>
        </div>
    )
}
